import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

const ABOUT_IMG = "/images/about_orkhan_working.jpg";

const fadeUp = {
  hidden: { opacity: 0, y: 38 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = { visible: { transition: { staggerChildren: 0.13 } } };

export default function About({ t, language }) {
  const highlights = t.about.highlights;

  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-20 md:py-32 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Image column */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="relative mr-3 mb-3 md:mr-0 md:mb-0">
              <img
                src={ABOUT_IMG}
                alt={t.about.name}
                className="w-full h-[380px] sm:h-[500px] md:h-[620px] object-cover"
                loading="lazy"
                data-testid="about-portrait"
              />
              {/* Gold frame accent */}
              <div
                className="absolute pointer-events-none border border-[#D4AF37]/20"
                style={{ bottom: "-10px", right: "-10px", top: "10px", left: "10px" }}
                aria-hidden="true"
              />
            </div>
          </motion.div>

          {/* Text column */}
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.p
              variants={fadeUp}
              className="text-xs tracking-[0.3em] sm:tracking-[0.38em] uppercase text-[#D4AF37] font-body mb-5"
            >
              {t.about.sectionTitle}
            </motion.p>

            <motion.h2
              variants={fadeUp}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-heading leading-tight mb-3"
              data-testid="about-title"
            >
              {t.about.name}
            </motion.h2>

            <motion.p variants={fadeUp} className="font-heading text-xl text-[#D4AF37]/70 italic mb-8">
              {t.about.subtitle}
            </motion.p>

            <motion.div variants={fadeUp} className="w-10 h-[1px] bg-[#D4AF37]/40 mb-8" />

            <motion.p
              variants={fadeUp}
              className="text-white/60 font-body leading-relaxed text-base md:text-lg mb-10"
              data-testid="about-text"
            >
              {t.about.description}
            </motion.p>

            <motion.div variants={stagger} className="space-y-1">
              {highlights.map((h, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="flex items-start gap-4 py-4 border-b border-white/5 last:border-0"
                  data-testid={`about-highlight-${i}`}
                >
                  <CheckCircle2 size={17} className="text-[#D4AF37] mt-0.5 shrink-0" />
                  <div>
                    <p className="text-white font-body font-medium text-base">{h.title}</p>
                    <p className="text-white/55 font-body text-xs tracking-widest mt-0.5 uppercase">
                      {h.subtitle}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
